import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
} from '@mui/material';
import { Check, Close, Refresh } from '@mui/icons-material';
import adminService from '../services/adminService';

const PromotionRequests = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminService.getPromotionRequests(filter);
      setRequests(res.requests || []);
    } catch (e) {
      setError('Failed to load promotion requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const approve = async (id) => {
    try {
      await adminService.approvePromotionRequest(id);
      await load();
    } catch (e) {
      setError('Failed to approve request');
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    try {
      await adminService.rejectPromotionRequest(rejecting._id, notes);
      setRejecting(null);
      setNotes('');
      await load();
    } catch (e) {
      setError('Failed to reject request');
    }
  };

  const typeLabel = (t) => (t ? t.toUpperCase() : '');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Promotion Requests</Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select value={filter} label="Status" onChange={(e) => setFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<Refresh />} onClick={load}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 0, borderRadius: 3, border: '1px solid #eee' }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Seller</TableCell>
                <TableCell>Promotion Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar src={req.seller?.profileImage} />
                      <Box>
                        <Typography fontWeight={600}>{req.seller?.brandName || req.seller?.name || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{req.seller?.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{typeLabel(req.type)}</TableCell>
                  <TableCell>{req.title}</TableCell>
                  <TableCell>{new Date(req.validFrom).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(req.validUntil).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={req.status.toUpperCase()}
                      color={req.status === 'approved' ? 'success' : req.status === 'rejected' ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {req.status === 'pending' ? (
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <IconButton color="success" onClick={() => approve(req._id)}><Check /></IconButton>
                        <IconButton color="error" onClick={() => setRejecting(req)}><Close /></IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No actions</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!rejecting} onClose={() => setRejecting(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button onClick={reject} color="error" variant="contained">Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromotionRequests;
